import type { CustomerCommentsData } from '@entities/customer'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useCustomerCommentsStore } from '../../model/comments/store'

interface CustomerCommentsApiOptions {
  customerId: string | number
  /** Continue the feed from `meta.next_cursor`; omitted = the first page. */
  cursor?: string | null
}

/**
 * `GET /admin-api/customers/{customer}/comments` — every discipline's stage comments, newest first,
 * cursor-paginated. A further page must repeat the same `disciplines[]` filter and `per_page`.
 */
export const useCustomerCommentsApi = async (options: CustomerCommentsApiOptions): Promise<CustomerCommentsData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useCustomerCommentsStore()
  const isNextPage = !!options.cursor

  const url = useAdminRoutes('admin.customer.comments-data', {
    customer_id: options.customerId,
    ...pick({
      disciplines: store.getFilterDisciplines.length ? store.getFilterDisciplines : undefined,
      per_page: store.getPerPage,
      cursor: options.cursor || undefined,
    }),
  })

  const setLoading = (value: boolean): void => {
    if (isNextPage) {
      store.setLoadingMore(value)
    } else {
      store.setLoading(value)
    }
  }

  setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const commentsData: CustomerCommentsData | null = data.data || null

    if (commentsData) {
      if (isNextPage) {
        store.appendComments(commentsData.comments)
      } else {
        store.setComments(commentsData.comments)
      }
      store.setNextCursor(commentsData.meta?.next_cursor ?? null)
    }

    return commentsData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    setLoading(false)
  }
}
